import React, { useState } from "react";
import styled from "styled-components";
import { useAuth } from "../contexts/AuthContext";

// Styled Components
const BORDER_RADIUS = "8px";
const PRIMARY_BG = "#fff";
const SECONDARY_BG = "#f3f4f6";
const ACCENT_BG = "#2563eb";
const ACCENT_TEXT = "#2563eb";
const GRAY_TEXT = "#6b7280";
const DARK_TEXT = "#111827";
const BOX_SHADOW = "0 4px 24px rgba(0,0,0,0.08), 0 1.5px 4px rgba(0,0,0,0.04)";

const ProfileContainer = styled.div`
  position: relative;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  border-radius: ${BORDER_RADIUS};
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 1rem;
  &:hover {
    background: ${SECONDARY_BG};
  }
`;

const Avatar = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  background: ${ACCENT_BG};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  font-weight: 600;
`;

const UserInfo = styled.div`
  display: none;
  text-align: left;
  @media (min-width: 768px) {
    display: block;
  }
`;

const UserName = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: ${DARK_TEXT};
  margin: 0;
`;

const UserEmail = styled.p`
  font-size: 0.85rem;
  color: ${GRAY_TEXT};
  margin: 0;
`;

const DropdownIcon = styled.svg`
  width: 1.1rem;
  height: 1.1rem;
  color: #9ca3af;
  transition: transform 0.2s;
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 0.5rem;
  width: 22rem;
  max-width: calc(100vw - 1rem);
  background: ${PRIMARY_BG};
  border-radius: ${BORDER_RADIUS};
  box-shadow: ${BOX_SHADOW};
  border: 1px solid #e5e7eb;
  z-index: 50;

  /* Mobile: fixed positioning to prevent clipping */
  @media (max-width: 640px) {
    position: fixed;
    top: calc(4rem + env(safe-area-inset-top, 0px) + 0.5rem);
    left: 0.5rem;
    right: 0.5rem;
    width: auto;
    max-width: none;
    max-height: calc(100vh - (4rem + env(safe-area-inset-top, 0px) + 1rem));
    overflow-y: auto;
    z-index: 1100; /* Above navigation */
  }
`;

const DropdownContent = styled.div`
  padding: 1.25rem 1.5rem 1.25rem 1.5rem;
`;

const UserHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const LargeAvatar = styled.div`
  width: 3.25rem;
  height: 3.25rem;
  background: ${ACCENT_BG};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
`;

const UserDetails = styled.div``;

const UserFullName = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${DARK_TEXT};
  margin: 0 0 0.25rem 0;
`;

const UserEmailLarge = styled.p`
  font-size: 0.95rem;
  color: ${GRAY_TEXT};
  margin: 0 0 0.5rem 0;
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${(props) => {
    switch (props.$role) {
      case "super_admin":
        return "#ede9fe";
      case "admin":
        return "#dbeafe";
      case "agent":
        return "#dcfce7";
      default:
        return "#f3f4f6";
    }
  }};
  color: ${(props) => {
    switch (props.$role) {
      case "super_admin":
        return "#7c3aed";
      case "admin":
        return "#1d4ed8";
      case "agent":
        return "#15803d";
      default:
        return "#374151";
    }
  }};
`;

const UserIdSection = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: ${BORDER_RADIUS};
`;

const UserIdLabel = styled.p`
  font-size: 0.8rem;
  color: ${GRAY_TEXT};
  margin: 0 0 0.25rem 0;
`;

const UserIdValue = styled.p`
  font-size: 0.95rem;
  font-family: monospace;
  color: #374151;
  margin: 0;
`;

const PermissionsSection = styled.div`
  margin-bottom: 1.25rem;
`;

const PermissionsTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: ${DARK_TEXT};
  margin: 0 0 0.75rem 0;
`;

const PermissionsContainer = styled.div`
  max-height: 12rem;
  overflow-y: auto;
`;

const PermissionGroup = styled.div`
  margin-bottom: 0.75rem;
`;

const PermissionGroupTitle = styled.h5`
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.25rem 0;
  text-transform: capitalize;
`;

const PermissionTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const PermissionTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.7rem;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 600;
  background: #e0e7ff;
  color: ${ACCENT_TEXT};
`;

const ActionsSection = styled.div`
  border-top: 1px solid #e5e7eb;
  padding-top: 1.25rem;
`;

const LogoutButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.2rem;
  border: 1px solid transparent;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 0.5rem;
  color: white;
  background: #2563eb;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 0.5rem;
  &:hover {
    background: #1d4ed8;
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }
`;

const LogoutIcon = styled.svg`
  width: 1.1rem;
  height: 1.1rem;
  margin-right: 0.5rem;
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
`;

const UserProfile = () => {
  const { user, permissions, role, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  if (!user) return null;

  // Group permissions by resource
  const groupedPermissions = permissions.reduce((acc, permission) => {
    const [resource] = permission.split(":");
    if (!acc[resource]) {
      acc[resource] = [];
    }
    acc[resource].push(permission);
    return acc;
  }, {});

  // Get role display name
  const getRoleDisplayName = (role) => {
    const roleNames = {
      super_admin: "Super Administrator",
      admin: "Administrator",
      agent: "Agent",
    };
    return roleNames[role] || role;
  };

  return (
    <ProfileContainer>
      {/* User Menu Button */}
      <ProfileButton onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
        <Avatar>
          {user.firstName?.charAt(0) || user.email?.charAt(0) || "U"}
        </Avatar>
        <UserInfo>
          <UserName>
            {user.firstName} {user.lastName}
          </UserName>
          <UserEmail>{user.email}</UserEmail>
        </UserInfo>
        <DropdownIcon
          $isOpen={isDropdownOpen}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </DropdownIcon>
      </ProfileButton>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <DropdownMenu>
          <DropdownContent>
            {/* User Info Header */}
            <UserHeader>
              <LargeAvatar>
                {user.firstName?.charAt(0) || user.email?.charAt(0) || "U"}
              </LargeAvatar>
              <UserDetails>
                <UserFullName>
                  {user.firstName} {user.lastName}
                </UserFullName>
                <UserEmailLarge>{user.email}</UserEmailLarge>
                <RoleBadge $role={role}>{getRoleDisplayName(role)}</RoleBadge>
              </UserDetails>
            </UserHeader>

            {/* User ID */}
            <UserIdSection>
              <UserIdLabel>User ID</UserIdLabel>
              <UserIdValue>{user.id}</UserIdValue>
            </UserIdSection>

            {/* Permissions Section */}
            <PermissionsSection>
              <PermissionsTitle>
                Permissions ({permissions.length})
              </PermissionsTitle>
              <PermissionsContainer>
                {Object.entries(groupedPermissions).map(([resource, perms]) => (
                  <PermissionGroup key={resource}>
                    <PermissionGroupTitle>{resource}</PermissionGroupTitle>
                    <PermissionTags>
                      {perms.map((permission) => (
                        <PermissionTag key={permission}>
                          {permission.split(":")[1]}
                        </PermissionTag>
                      ))}
                    </PermissionTags>
                  </PermissionGroup>
                ))}
              </PermissionsContainer>
            </PermissionsSection>

            {/* Actions */}
            <ActionsSection>
              <LogoutButton onClick={logout}>
                <LogoutIcon
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </LogoutIcon>
                Sign Out
              </LogoutButton>
            </ActionsSection>
          </DropdownContent>
        </DropdownMenu>
      )}

      {/* Backdrop to close dropdown */}
      {isDropdownOpen && <Backdrop onClick={() => setIsDropdownOpen(false)} />}
    </ProfileContainer>
  );
};

export default UserProfile;
